// =============================================
// coaTemplateApi — typed client for /api/bookkeeping/*.
// Business-type → starting Chart of Accounts: read the merged template for a business
// type, and instantiate a (user-editable) staged list into the owner's acctg_coa.
// Mirrors payrollApi: bearer token from localStorage, JSON in/out. Source of truth is
// PostgreSQL; there is no localStorage cache for these calls.
// =============================================
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
function token(): string | null { return localStorage.getItem('akt_auth_token'); }

async function call<T>(path: string, opts: RequestInit = {}): Promise<T> {
  const r = await fetch(`${API_BASE}/bookkeeping${path}`, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token()}`,
      ...(opts.headers || {}),
    },
  });
  if (!r.ok) {
    const body = await r.json().catch(() => ({} as any));
    const e: any = new Error(body.message || `Request failed (${r.status})`);
    e.code = r.status;
    throw e;
  }
  return r.json();
}

export interface TemplateAccount {
  template_id: number;
  template_code: string;
  scope: 'core' | 'category' | 'type';
  account_kind: 'asset' | 'liability' | 'equity' | 'income' | 'other_income' | 'cogs' | 'expense';
  suggested_number: string;
  account_name: string;
  account_description: string;
  finsta_cate_id: number;
  finsta_cate_name: string;
  normal_balance: 'Debit' | 'Credit';
  preselected: boolean;
  /** Suggested tax-return code per code list, e.g. { GIFI: '1002' } (country tax pack, migration 145). */
  tax_lines?: Record<string, string>;
}

export interface TemplateSection {
  key: string;
  name: string;
  normal_balance: 'Debit' | 'Credit';
  accounts: TemplateAccount[];
}

export interface BusiTypeFlags {
  has_inventory: boolean;
  has_cogs: boolean;
  handles_tips: boolean;
  has_gift_cards: boolean;
  uses_job_costing: boolean;
}

export interface CoaTemplateResponse {
  busi_type: {
    busi_type_id: number;
    type_code: string;
    type_name: string;
    naics_code: string | null;
    busi_category_id: number;
    category_code: string;
    category_name: string;
    flags: BusiTypeFlags;
  };
  sections: TemplateSection[];
}

export interface BusiCategory {
  busi_category_id: number;
  category_code: string;
  category_name: string;
  sort_order: number;
}
export interface BusiType {
  busi_type_id: number;
  busi_category_id: number;
  type_code: string;
  type_name: string;
  naics_code: string | null;
  has_inventory: number;
  has_cogs: number;
  handles_tips: number;
  has_gift_cards: number;
  uses_job_costing: number;
  sort_order: number;
  /** Other words people type for this type (comma list) — searched by the picker. */
  search_keywords?: string | null;
}

/** Reference list for the Type of Business picker (categories + all types). */
export function getBusinessTypes(): Promise<{ categories: BusiCategory[]; types: BusiType[] }> {
  return call(`/business-types`);
}

/**
 * Fetch the merged starting-COA template for one or several business types (an entity
 * can run more than one activity). The accounts of every type are kept; the same account
 * is listed once and clashing numbers are moved apart by the server.
 */
export function getCoaTemplate(busiTypeIds: number[]): Promise<CoaTemplateResponse> {
  return call<CoaTemplateResponse>(`/coa-template?busi_type_ids=${encodeURIComponent(busiTypeIds.join(','))}`);
}

export interface CreateFromTemplateAccount {
  account_kind: TemplateAccount['account_kind'];
  suggested_number: string;
  account_name: string;
  account_description?: string;
  /** other names (comma list) / free labels (comma list) — see migration 133 */
  alias_names?: string;
  tags?: string;
  finsta_cate_id: number;
  /** This row's id within the request, so another row can name it as its parent. */
  ref?: string;
  /** `ref` of the account this one is a sub-account of (same section); null = under the section. */
  parent_ref?: string | null;
  /** Tax-return code per code list ({ GIFI: '1002' }); saved only for lists these books use. */
  tax_lines?: Record<string, string>;
}

export interface CreateFromTemplateResult {
  ok: boolean;
  headers_created: number;
  accounts_created: number;
  skipped: number;
}

/**
 * Instantiate the staged (possibly edited) account list into acctg_coa for one owner.
 * Builds a header + child tree, skipping any accounts that already exist for that owner
 * (safe to re-run to add only the missing accounts).
 */
export function createCoaFromTemplate(params: {
  owner_type: string;
  owner_id: number;
  busi_type_id?: number;
  accounts: CreateFromTemplateAccount[];
}): Promise<CreateFromTemplateResult> {
  return call<CreateFromTemplateResult>(`/coa/from-template`, {
    method: 'POST',
    body: JSON.stringify(params),
  });
}

/**
 * Set (or clear, with busi_type_id = null) the industry type of a business, or of a
 * person who needs books of their own (e.g. an individual with rental income).
 * Owner-scoped endpoint — works for any account owner, not just site admins.
 */
export function setBusinessType(params: {
  owner_type: string;
  owner_id: number;
  /** Every type chosen, main activity first. An empty list clears the type. */
  busi_type_ids: number[];
}): Promise<{ ok: boolean; business_id?: number; user_id?: number; industry_type_id: number | null; industry_type_ids: number[] }> {
  return call(`/business-type`, {
    method: 'PUT',
    body: JSON.stringify(params),
  });
}

// ---- Setup GL Accounts draft (migration 144) ----------------------------------
// The caller's own unsaved staged list for one set of books, kept on the server until
// Create, so a reload / sign-out / another device loses nothing.
export interface CoaSetupDraft {
  busi_type_ids: string;
  data: { sections?: unknown[] };
  updated_at: string;
}
const draftQuery = (owner_type: string, owner_id: number) =>
  `owner_type=${encodeURIComponent(owner_type)}&owner_id=${encodeURIComponent(String(owner_id))}`;

export function getCoaSetupDraft(owner_type: string, owner_id: number): Promise<CoaSetupDraft | null> {
  return call<CoaSetupDraft | null>(`/coa-setup-draft?${draftQuery(owner_type, owner_id)}`);
}
export function saveCoaSetupDraft(params: {
  owner_type: string; owner_id: number; busi_type_ids: number[]; data: { sections: unknown[] };
}): Promise<{ ok: boolean; updated_at: string }> {
  return call(`/coa-setup-draft`, { method: 'PUT', body: JSON.stringify(params) });
}
export function clearCoaSetupDraft(owner_type: string, owner_id: number): Promise<{ ok: boolean }> {
  return call(`/coa-setup-draft?${draftQuery(owner_type, owner_id)}`, { method: 'DELETE' });
}

// ---- Tax-return code lists (country tax pack, migration 145) -------------------
// Which code lists the books of one person / business use (Canada: GIFI for a
// corporation or partnership), with every code. Empty `forms` = none apply.
export interface TaxCodeLine {
  line_code: string;
  line_name: string;
  line_group: string | null;
  /** 'BS' balance sheet (assets, liabilities, equity) · 'IS' income statement */
  statement: 'BS' | 'IS';
  is_heading: number;
  includes_text: string | null;
  /** the account's own edits (migration 148); CRA's line_name stays as published */
  own_name?: string | null;
  own_note?: string | null;
  is_hidden?: number;
  /** vs the version before: 'added' | 'renamed' (Tax Codes tab) */
  changed?: 'added' | 'renamed' | null;
}
/** One version of a code list (GIFI 2011-2017, 2018-current). */
export interface TaxCodeVersion {
  tax_version_id?: number;
  version_code: string;
  version_label: string;
  effective_from: string;
  effective_to: string | null;
  source_ref: string | null;
  notes: string | null;
  codes?: number;
}
export interface TaxCodeForm {
  tax_form_id: number;
  tax_form_code: string;
  form_name: string;
  description: string | null;
  source_ref: string | null;
  /** the version these lines are from (the one in force on the date asked, default today) */
  version?: TaxCodeVersion | null;
  lines: TaxCodeLine[];
}
export interface TaxCodeContext {
  country_id: number | null;
  /** the business's (person's) country as named in site_mstr_countries */
  country_name: string | null;
  structure: string | null;
  /** 'tax_code' = the business numbers its accounts by the code (migration 146) */
  numbering: 'own' | 'tax_code';
  is_business: boolean;
  /** accounting year end 'MM-DD' (people: 12-31) */
  fiscal_year_end: string;
  /** every code list of the business's country (the "number by code" option needs one) */
  available_forms: { tax_form_code: string; form_name: string }[];
  /** the lists that apply to these books, with their codes */
  forms: TaxCodeForm[];
}
export function getTaxCodes(owner_type: string, owner_id: number, asOf?: string): Promise<TaxCodeContext> {
  return call<TaxCodeContext>(`/tax-codes?owner_type=${encodeURIComponent(owner_type)}&owner_id=${encodeURIComponent(String(owner_id))}${asOf ? `&as_of=${encodeURIComponent(asOf)}` : ''}`);
}

/** Every account's tax-return code in the caller's account (all sets of books). */
export interface CoaTaxLine { coa_id: number; tax_form_code: string; line_code: string }
export function getCoaTaxLines(): Promise<CoaTaxLine[]> {
  return call<CoaTaxLine[]>(`/coa-tax-lines`);
}
/** Set one account's code for one code list; '' clears it. */
export function saveCoaTaxLine(coa_id: number, tax_form_code: string, line_code: string): Promise<{ ok: boolean; line_code: string | null }> {
  return call(`/coa-tax-line`, { method: 'PUT', body: JSON.stringify({ coa_id, tax_form_code, line_code }) });
}
/** coa_id -> { GIFI: '1002', … } */
export function taxLinesByAccount(rows: CoaTaxLine[]): Map<number, Record<string, string>> {
  const m = new Map<number, Record<string, string>>();
  rows.forEach(r => {
    const id = Number(r.coa_id);
    m.set(id, { ...(m.get(id) || {}), [r.tax_form_code]: r.line_code });
  });
  return m;
}

/** A business numbers its accounts by its own numbers ('own') or by the tax-return code ('tax_code'). */
export function setCoaNumbering(owner_type: string, owner_id: number, numbering: 'own' | 'tax_code'): Promise<{ ok: boolean; numbering: string }> {
  return call(`/coa-numbering`, { method: 'PUT', body: JSON.stringify({ owner_type, owner_id, numbering }) });
}

/** A business's accounting year end ('MM-DD'; null / '12-31' = 31 December). */
export function setFiscalYearEnd(owner_type: string, owner_id: number, fiscal_year_end: string | null): Promise<{ ok: boolean; fiscal_year_end: string; next_year_end: string }> {
  return call(`/fiscal-year-end`, { method: 'PUT', body: JSON.stringify({ owner_type, owner_id, fiscal_year_end }) });
}
/** The year-end records of one set of books (migration 147). */
export interface CoaYearEnds {
  fiscal_year_end: string;
  next_year_end: string;
  /** year ends before this date were never saved */
  first_from: string;
  /** the last year end closed (saved); null = none yet */
  last_closed: string | null;
  years: { year_end_date: string; accounts: number }[];
}
export function getCoaYearEnds(owner_type: string, owner_id: number): Promise<CoaYearEnds> {
  return call(`/coa-year-ends?owner_type=${encodeURIComponent(owner_type)}&owner_id=${encodeURIComponent(String(owner_id))}`);
}
export interface CoaYearEndRow {
  coa_id: number; account_number: string | null; own_number: string | null; account_name: string;
  parent_coa_id: number | null; is_header: number; numbering: string;
  tax_form_code: string | null; line_code: string | null; code_number: string | null;
}
export function getCoaYearEndRows(owner_type: string, owner_id: number, date: string): Promise<{ rows: CoaYearEndRow[] }> {
  return call(`/coa-year-ends?owner_type=${encodeURIComponent(owner_type)}&owner_id=${encodeURIComponent(String(owner_id))}&date=${encodeURIComponent(date)}`);
}

/** What hangs on one account (shown before its tax-return code is changed). */
export interface CoaDependants {
  account: { coa_id: number; account_number: string; account_name: string; current_balance: string | number; normal_balance: string };
  journal_lines: number;
  bank_txns: number;
  documents: number;
  sub_accounts: number;
  party_defaults: number;
  nature_links: number;
  /** year ends already closed for the account, with the code saved then */
  closed_years: { d: string; line_code: string | null }[];
}
export function getCoaDependants(coa_id: number): Promise<CoaDependants> {
  return call(`/coa-dependants?coa_id=${encodeURIComponent(String(coa_id))}`);
}

/** The code lists of the account's countries (Tax Codes tab). */
export interface TaxCodeList {
  tax_form_id: number; tax_form_code: string; form_name: string; description: string | null;
  applies_structures: string | null; source_ref: string | null; country_id: number; country_name: string;
  versions: TaxCodeVersion[]; current_version_code: string | null;
}
export function getTaxCodeLists(): Promise<TaxCodeList[]> {
  return call(`/tax-code-lists`);
}
export function getTaxCodeList(tax_form_id: number, version_code?: string): Promise<{ version: TaxCodeVersion | null; previous: string | null; lines: TaxCodeLine[]; removed: { line_code: string; line_name: string }[] }> {
  return call(`/tax-code-list?tax_form_id=${encodeURIComponent(String(tax_form_id))}${version_code ? `&version_code=${encodeURIComponent(version_code)}` : ''}`);
}
export function saveTaxLineEdit(p: { tax_form_id: number; line_code: string; own_name: string; own_note: string; is_hidden: boolean }): Promise<{ ok: boolean }> {
  return call(`/tax-line-edit`, { method: 'PUT', body: JSON.stringify(p) });
}
